import { createContext, useContext, FC, ReactNode } from 'react'
import { APP_TYPES, AppType } from '@/types/appType'
import { useSettingQuery } from '@/features/auth'

// AppType dispatcher.
// 1 次 source: server flag (= custom_setting.use_legacy_ui).
//   True (= default) → LEGACY shell
//   False → LATEST shell
// 2 次 (dev only): localStorage('yakureki_app_type'). production build では disable.
//
// undefined fallback も True 扱い (= setting 読込前 / setting 不在時は LEGACY).
// ramp 終着点 (= ε 段階) で flag 削除 + LEGACY 物理 rm = legacy 完全廃止.

interface AppTypeContextValue {
  appType: AppType
  isLoading: boolean
}

const AppTypeContext = createContext<AppTypeContextValue | undefined>(undefined)

const readLocalOverride = (): AppType | null => {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return null
  const stored = localStorage.getItem('yakureki_app_type')
  if (stored === APP_TYPES.LATEST || stored === APP_TYPES.LEGACY) return stored
  return null
}

export const AppTypeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: setting, isLoading } = useSettingQuery()

  const localOverride = readLocalOverride()
  // use_legacy_ui: default True (= LEGACY) / False (= LATEST) / undefined fallback も True.
  const serverType =
    setting?.use_legacy_ui === false ? APP_TYPES.LATEST : APP_TYPES.LEGACY
  const appType = localOverride ?? serverType

  return (
    <AppTypeContext.Provider value={{ appType, isLoading }}>
      {children}
    </AppTypeContext.Provider>
  )
}

export const useAppType = () => {
  const ctx = useContext(AppTypeContext)
  if (!ctx) throw new Error('useAppType must be used within AppTypeProvider')
  return ctx
}
